import {
  Alert,
  Button,
  EmptyState,
  Modal,
  ResponsiveList,
  useToast,
  type ResponsiveListColumn,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';

import { useCurrentUser } from '../auth/use-current-user';
import { describeOrderError } from '../orders/error-messages';
import { hasOrderPermission } from '../orders/order-permissions';
import { usePortalMe } from '../shell/use-portal-me';
import {
  createOrderTemplate,
  deleteOrderTemplate,
  listOrderTemplates,
  updateOrderTemplate,
  type OrderTemplate,
} from './api';
import { TemplateFormModal, type TemplateFormModalValues } from './template-form-modal';

type ModalState = { mode: 'create' } | { mode: 'edit'; template: OrderTemplate } | null;

const DAY_LABELS: Record<number, string> = {
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb',
  7: 'Dom',
};

function formatDays(daysOfWeek: number[]): string {
  if (daysOfWeek.length === 0) {
    return '—';
  }

  return [...daysOfWeek]
    .sort((a, b) => a - b)
    .map((day) => DAY_LABELS[day] ?? String(day))
    .join(', ');
}

function toFormValues(
  template: OrderTemplate | undefined,
  defaultSiteId: string,
): TemplateFormModalValues {
  if (template === undefined) {
    return { siteId: defaultSiteId, name: '', daysOfWeek: [], isActive: true, items: [] };
  }

  return {
    siteId: template.siteId,
    ...(template.operatingUnitId === undefined ? {} : { operatingUnitId: template.operatingUnitId }),
    name: template.name,
    daysOfWeek: template.daysOfWeek,
    isActive: template.isActive,
    items: template.items,
  };
}

/**
 * `portal-templates/spec.md` → "Pantalla de plantillas en el portal":
 * ABM sin ningún botón de generación, visible solo con
 * `order.template.manage` y `canOrder = true`. No hay verificación de
 * ruta separada aquí — `AppShell` ya condiciona el link de navegación
 * hacia esta pantalla por el mismo permiso (`portal-access/spec.md` →
 * "Interfaz condicionada por permisos, no por el nombre del rol").
 */
export function TemplateListPage(): JSX.Element {
  const currentUser = useCurrentUser();
  const me = usePortalMe(true);
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalState>(null);
  const [deleting, setDeleting] = useState<OrderTemplate | null>(null);
  const { toast } = useToast();

  const query = useQuery({ queryKey: ['order-templates'], queryFn: listOrderTemplates });

  const roleCodes = currentUser.data?.roleCodes ?? [];
  const canManage = hasOrderPermission(roleCodes, 'order.template.manage');
  const sites = me.data?.status === 'ACTIVE' ? me.data.sites : [];
  const operatingUnits = me.data?.status === 'ACTIVE' ? (me.data.operatingUnits ?? []) : [];
  const showUnits = operatingUnits.length > 1;

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['order-templates'] });
  }

  const createMutation = useMutation({
    mutationFn: (input: TemplateFormModalValues) =>
      createOrderTemplate({
        siteId: input.siteId,
        ...(input.operatingUnitId ? { operatingUnitId: input.operatingUnitId } : {}),
        name: input.name,
        daysOfWeek: input.daysOfWeek,
        items: input.items,
      }),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Plantilla guardada.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: TemplateFormModalValues }) =>
      updateOrderTemplate(id, {
        name: input.name,
        daysOfWeek: input.daysOfWeek,
        isActive: input.isActive,
        items: input.items,
      }),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Plantilla guardada.');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (template: OrderTemplate) =>
      updateOrderTemplate(template.id, { isActive: !template.isActive }),
    onSuccess: async (_result, template) => {
      await invalidate();
      toast('success', template.isActive ? 'Plantilla desactivada.' : 'Plantilla activada.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteOrderTemplate(id),
    onSuccess: async () => {
      await invalidate();
      setDeleting(null);
      toast('success', 'Plantilla eliminada.');
    },
  });

  const columns: ResponsiveListColumn<OrderTemplate>[] = [
    { key: 'name', header: 'Nombre', render: (template) => template.name },
    ...(showUnits
      ? [
          {
            key: 'operatingUnit',
            header: 'Punto de venta',
            render: (template: OrderTemplate) => template.operatingUnitName ?? '—',
          },
        ]
      : []),
    { key: 'days', header: 'Días', render: (template) => formatDays(template.daysOfWeek) },
    {
      key: 'status',
      header: 'Estado',
      render: (template) => (template.isActive ? 'Activa' : 'Inactiva'),
    },
  ];

  function renderActions(template: OrderTemplate): JSX.Element {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            updateMutation.reset();
            setModal({ mode: 'edit', template });
          }}
        >
          Editar
        </Button>
        <Button
          variant="secondary"
          size="sm"
          loading={toggleActiveMutation.isPending && toggleActiveMutation.variables?.id === template.id}
          onClick={() => toggleActiveMutation.mutate(template)}
        >
          {template.isActive ? 'Desactivar' : 'Activar'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            deleteMutation.reset();
            setDeleting(template);
          }}
        >
          Eliminar
        </Button>
      </div>
    );
  }

  const actionError = toggleActiveMutation.error;

  const formError =
    modal?.mode === 'create'
      ? createMutation.error
      : modal?.mode === 'edit'
        ? updateMutation.error
        : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-slate-900">Plantillas de pedido</h1>
        {canManage ? (
          <Button
            onClick={() => {
              createMutation.reset();
              setModal({ mode: 'create' });
            }}
          >
            Nueva plantilla
          </Button>
        ) : null}
      </div>

      {query.isError ? (
        <Alert tone="danger">
          No pudimos cargar las plantillas.
          <div className="mt-2">
            <Button size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? <p className="text-sm text-slate-500">Cargando…</p> : null}

      {query.data !== undefined && query.data.length === 0 ? (
        <EmptyState headingLevel="h2" title="Todavía no hay plantillas cargadas." />
      ) : null}

      {actionError !== null ? <Alert tone="danger">{describeOrderError(actionError)}</Alert> : null}

      {query.data !== undefined && query.data.length > 0 ? (
        <ResponsiveList
          items={query.data}
          rowKey={(template) => template.id}
          columns={columns}
          renderCardHeader={(template) => template.name}
          renderCardBody={(template) => (
            <>
              {showUnits ? (
                <>
                  <span className="text-slate-500">Punto de venta</span>
                  <span>{template.operatingUnitName ?? '—'}</span>
                </>
              ) : null}
              <span className="text-slate-500">Días</span>
              <span>{formatDays(template.daysOfWeek)}</span>
              <span className="text-slate-500">Estado</span>
              <span>{template.isActive ? 'Activa' : 'Inactiva'}</span>
            </>
          )}
          {...(canManage ? { renderActions } : {})}
          emptyState={<EmptyState headingLevel="h2" title="Todavía no hay plantillas cargadas." />}
          caption="Plantillas de pedido"
        />
      ) : null}

      {deleting !== null ? (
        <Modal open onClose={() => setDeleting(null)} title="Eliminar plantilla">
          <div className="flex flex-col gap-4">
            {deleteMutation.isError ? (
              <Alert tone="danger">{describeOrderError(deleteMutation.error)}</Alert>
            ) : null}
            <p className="text-sm text-slate-700">¿Eliminar la plantilla "{deleting.name}"?</p>
            <div className="flex items-center justify-end gap-3">
              <Button variant="secondary" onClick={() => setDeleting(null)}>
                Cancelar
              </Button>
              <Button
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate(deleting.id)}
              >
                Sí, eliminar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}

      {modal !== null ? (
        <TemplateFormModal
          mode={modal.mode}
          sites={sites}
          operatingUnits={operatingUnits}
          defaultValues={toFormValues(
            modal.mode === 'edit' ? modal.template : undefined,
            sites[0]?.id ?? '',
          )}
          submitting={createMutation.isPending || updateMutation.isPending}
          error={formError}
          onClose={() => {
            createMutation.reset();
            updateMutation.reset();
            setModal(null);
          }}
          onSubmit={async (values) => {
            if (modal.mode === 'create') {
              await createMutation.mutateAsync(values).catch(() => undefined);
            } else {
              await updateMutation
                .mutateAsync({ id: modal.template.id, input: values })
                .catch(() => undefined);
            }
          }}
        />
      ) : null}
    </div>
  );
}
